import React from "react";
import Button from "@/components/shared/Button";
import { useTranslation } from "react-i18next";
import { FiZap, FiBriefcase } from "react-icons/fi";
import { HiOutlineUsers, HiOutlineBuildingOffice2 } from "react-icons/hi2";

const planConfig = {
  monthly: {
    icon: FiZap,
    titleKey: "pricing.monthlyPlan",
    descKey: "pricing.monthlyDesc",
    billedKey: "pricing.billed_monthly",
  },
  halfYear: {
    icon: FiBriefcase,
    titleKey: "pricing.semiAnnualPlan",
    descKey: "pricing.halfYearDesc",
    billedKey: "pricing.billed_semiAnnual",
  },
  yearly: {
    icon: HiOutlineBuildingOffice2,
    titleKey: "pricing.annualPlan",
    descKey: "pricing.yearlyDesc",
    billedKey: "pricing.billed_annual",
  },
};

const formatPrice = (value, isAR) => {
  const number = Number(value || 0);
  return new Intl.NumberFormat(isAR ? "ar-EG" : "en-US").format(number);
};

const PricingCard = ({ plan, type = "monthly", highlight = false }) => {
  const { t, i18n } = useTranslation();
  const isAR = i18n.language?.startsWith("ar");

  const config = planConfig[type] || planConfig.monthly;
  const Icon = config.icon;

  const price =
    plan?.pricing?.[type] ??
    plan?.price?.[type] ??
    plan?.prices?.[type] ??
    null;

  const features = plan?.featuresByDuration?.[type] || plan?.features || [];

  return (
    <div
      className={`flex min-h-[390px] w-full flex-col rounded-lg border p-5 text-center shadow-sm transition-all duration-300 sm:p-6 ${
        highlight
          ? "scale-100 border-[#162536] bg-[#162536] text-white shadow-xl lg:scale-105"
          : "border-gray-200 bg-white text-gray-950 hover:-translate-y-1 hover:shadow-lg"
      }`}
    >
      <div
        className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${
          highlight ? "bg-white text-[#162536]" : "bg-[#162536] text-white"
        }`}
      >
        <Icon className="text-xl" />
      </div>

      <h3 className="mt-4 text-lg font-bold sm:text-xl">
        {t(config.titleKey)}
      </h3>

      <p
        className={`mx-auto mt-4 min-h-[48px] max-w-[240px] text-sm leading-6 ${
          highlight ? "text-gray-200" : "text-gray-700"
        }`}
      >
        {features?.[0] || t(config.descKey)}
      </p>

      <div
        className={`my-6 h-px w-full ${
          highlight ? "bg-white/20" : "bg-gray-100"
        }`}
      />

      <div className="flex items-end justify-center gap-1">
        <span className="text-3xl font-extrabold">
          {price === null
            ? t("pricing.notAvailable") || "N/A"
            : `${t("pricing.calculator.currency")} ${formatPrice(price, isAR)}`}
        </span>

        <span
          className={`mb-1 text-sm font-semibold ${
            highlight ? "text-gray-200" : "text-gray-900"
          }`}
        >
          /{t(`pricing.${type}`)}
        </span>
      </div>

      <p
        className={`mt-2 text-sm ${
          highlight ? "text-gray-300" : "text-gray-500"
        }`}
      >
        {price === null ? "" : t(config.billedKey)}
      </p>

      <div
        className={`my-6 h-px w-full ${
          highlight ? "bg-white/20" : "bg-gray-100"
        }`}
      />

      <div className="mb-8 flex justify-center">
        <div
          className={`inline-flex items-center gap-2 rounded px-4 py-2 text-sm font-medium ${
            highlight ? "bg-white/15 text-white" : "bg-purple-50 text-gray-900"
          }`}
        >
          <HiOutlineUsers
            className={highlight ? "text-white" : "text-purple-500"}
          />

          <span>
            {t("pricing.plan")} {formatPrice(plan?.employees, isAR)}{" "}
            {t("pricing.employee")}
          </span>
        </div>
      </div>

      <div className="mt-auto flex justify-center">
        <Button
          url="/book-demo"
          variant={highlight ? "pricingFeatured" : "pricingOutline"}
          className="w-full rounded-full px-6 py-3 text-sm font-bold sm:w-full"
        >
          {t("pricing.cta")}
        </Button>
      </div>
    </div>
  );
};

export default PricingCard;
